import type { englishResources } from '../i18n/resources';
import type { NavIconId } from './navIcons';

type LayoutStrings = (typeof englishResources)['layout'];

export type NavLabelKey = keyof LayoutStrings['nav']['items'];
export type NavSectionKey = keyof LayoutStrings['nav']['sections'];
export type PageId = keyof LayoutStrings['pages'];
export type HeaderActionKey = keyof LayoutStrings['actions'];

export type NavItem = {
  to: string;
  label: NavLabelKey;
  icon: NavIconId;
  end?: boolean;
  /** When true, page is a design-matched shell without live product APIs yet. */
  comingSoon?: boolean;
};

export type NavSection = {
  title: NavSectionKey;
  items: readonly NavItem[];
};

export type HeaderAction = {
  label: HeaderActionKey;
  to: string;
};

export type PageMeta = {
  id: PageId;
  /** When omitted, the header primary CTA falls back to Show something. */
  primaryAction?: HeaderAction;
  secondaryAction?: HeaderAction;
};

/**
 * Sidebar sections — Ghost Screen - Home.dc.html SB_SECTIONS.
 */
export const NAV_SECTIONS: readonly NavSection[] = [
  {
    title: 'workspace',
    items: [
      { to: '/', label: 'home', end: true, icon: 'home' },
      { to: '/screens', label: 'screens', icon: 'screens' },
      { to: '/content', label: 'library', icon: 'library' },
      { to: '/playlists', label: 'playlists', icon: 'playlists' },
      { to: '/schedule', label: 'schedule', icon: 'schedule' },
    ],
  },
  {
    title: 'manage',
    items: [
      { to: '/groups', label: 'groups', icon: 'groups' },
      { to: '/video-walls', label: 'videoWalls', icon: 'videoWalls' },
      { to: '/layouts', label: 'layouts', icon: 'layouts' },
      { to: '/designer', label: 'designer', icon: 'designer' },
      { to: '/command-operations', label: 'remoteFixes', icon: 'remoteFixes' },
    ],
  },
  {
    title: 'organisation',
    items: [
      { to: '/reports', label: 'reports', icon: 'reports' },
      { to: '/activity', label: 'activity', icon: 'activity' },
      { to: '/teams', label: 'teams', icon: 'teams' },
      { to: '/settings', label: 'settings', icon: 'settings' },
      { to: '/admin', label: 'admin', icon: 'admin' },
    ],
  },
] as const;

/** Flat list for route/meta lookups. */
export const NAV_ITEMS: readonly NavItem[] = NAV_SECTIONS.flatMap((section) => section.items);

/** @deprecated Prefer NAV_SECTIONS — kept for mobile overflow helpers. */
export const NAV_MAIN: readonly NavItem[] = NAV_SECTIONS[0]!.items;

/** @deprecated Prefer NAV_SECTIONS — kept for mobile overflow helpers. */
export const NAV_MORE: readonly NavItem[] = NAV_SECTIONS.slice(1).flatMap((section) => section.items);

export const DEFAULT_PRIMARY_ACTION: HeaderAction = { label: 'showSomething', to: '/playlists#new' };

export const PAGE_META: Record<string, PageMeta> = {
  '/': { id: 'home', primaryAction: DEFAULT_PRIMARY_ACTION },
  '/screens': {
    id: 'screens',
    primaryAction: { label: 'addDisplay', to: '/screens#setup' },
    secondaryAction: { label: 'addLocation', to: '/screens#location' },
  },
  '/content': { id: 'content', primaryAction: { label: 'upload', to: '/content#upload' } },
  '/playlists': { id: 'playlists', primaryAction: { label: 'newPlaylist', to: '/playlists#new' } },
  '/fleet': { id: 'fleet', primaryAction: DEFAULT_PRIMARY_ACTION },
  '/command-operations': { id: 'remoteFixes' },
  '/groups': { id: 'groups', primaryAction: { label: 'newGroup', to: '/groups#new' } },
  '/layouts': { id: 'layouts', primaryAction: { label: 'newLayout', to: '/layouts#new' } },
  '/schedule': { id: 'schedule', primaryAction: { label: 'newDaypart', to: '/schedule?new=1' } },
  '/video-walls': { id: 'videoWalls', primaryAction: { label: 'newWall', to: '/video-walls#new' } },
  '/reports': { id: 'reports', primaryAction: { label: 'downloadCsv', to: '/reports#export' } },
  '/designer': { id: 'designer' },
  '/activity': { id: 'activity' },
  '/teams': {
    id: 'teams',
    primaryAction: { label: 'admin', to: '/admin' },
    secondaryAction: { label: 'settings', to: '/settings' },
  },
  '/settings': { id: 'settings' },
  '/admin': {
    id: 'admin',
    primaryAction: { label: 'settings', to: '/settings' },
    secondaryAction: { label: 'home', to: '/' },
  },
};

export function getPageMeta(pathname: string): PageMeta {
  if (/^\/screens\/[^/]+$/.test(pathname)) return { id: 'manage' };
  return PAGE_META[pathname] ?? { id: 'fallback', primaryAction: DEFAULT_PRIMARY_ACTION };
}

export function getNavItem(pathname: string): NavItem | undefined {
  return NAV_ITEMS.find((item) =>
    item.end ? pathname === item.to : pathname === item.to || pathname.startsWith(`${item.to}/`),
  );
}
